/** Shared App/API/MCP contract. Books reference real question cards; ink belongs to the book. */
export const NOTEBOOK_MODULES = ['综合套卷', '资料分析', '数量关系', '判断推理', '言语理解', '常识判断', '申论', '复盘整理']
export interface NotebookBook {
  schema: 1
  module: string
  questionIds: string[]
  cover?: string
  sourceFile?: string
  importId?: string
  showQuestionNotes?: boolean
}
export interface NotebookInput {
  title: string
  module: string
  questionIds: string[]
  cover?: string
  sourceFile?: string
  notebookInk?: string
  notebookQuestionInk?: Record<string, string>
  notebookLayout?: Record<string, { imageScale: number }>
  showQuestionNotes?: boolean
}
export interface NotebookImportJob {
  state: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  title: string
  module: string
  fileName: string
  fingerprint: string
  totalPages: number
  processedPages: number
  questionCount: number
  bookId?: string
  error?: string
}
export function notebookModule(value: unknown): string {
  if (typeof value !== 'string' || !value.trim() || value.trim().length > 60 || /[\u0000-\u001f]/.test(value)) throw new Error('请选择有效的所属模块')
  return value.trim()
}
export function notebookQuestionIds(value: unknown): string[] {
  if (!Array.isArray(value) || !value.length || value.length > 1000
    || value.some(id => typeof id !== 'string' || !id || id.length > 200)) throw new Error('错题本须包含 1–1000 道题目')
  if (new Set(value).size !== value.length) throw new Error('同一错题本中不能重复添加相同题目')
  return value as string[]
}
export function notebookTitle(value: unknown): string {
  if (typeof value !== 'string' || !value.trim() || value.trim().length > 120) throw new Error('请输入 1–120 字的错题本名称')
  return value.trim()
}
export function notebookCover(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value !== 'string' || value.length > 1_500_000 || !/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(value)) throw new Error('封面须为 1MB 以内的 PNG/JPEG/WebP 图片')
  return value
}
/** Shared validation for REST, local writes and MCP; omitted fields preserve existing data. */
export function validateNotebookContent(input: Partial<NotebookInput> & { notebookProgress?: { lastQuestionId?: string; resetAt?: Record<string, number> } }) {
  if(input.showQuestionNotes !== undefined && typeof input.showQuestionNotes !== 'boolean')throw Error('笔记显示开关须为布尔值')
  const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
  if (input.sourceFile !== undefined && (typeof input.sourceFile !== 'string' || input.sourceFile.length > 240)) throw new Error('来源文件名须在 240 字以内')
  if (input.notebookInk !== undefined) {
    if (typeof input.notebookInk !== 'string') throw new Error('笔迹须为矢量 JSON 字符串')
    if (input.notebookInk && !/^data:image\/(png|jpeg|webp|svg\+xml)[;,]/.test(input.notebookInk)) {
      let ink: unknown
      try { ink = JSON.parse(input.notebookInk) } catch { throw new Error('笔迹 JSON 无效') }
      if (!record(ink) || ink.format !== 'notebook-page-ink-v1' || !Array.isArray(ink.strokes) || !Array.isArray(ink.legacyErasures)) throw new Error('笔迹格式须为 notebook-page-ink-v1')
    }
  }
  if (input.notebookQuestionInk !== undefined && (!record(input.notebookQuestionInk) || Object.values(input.notebookQuestionInk).some(v => typeof v !== 'string'))) throw new Error('旧题目笔迹须为题目 ID 与字符串的映射')
  if (input.notebookLayout !== undefined && (!record(input.notebookLayout) || Object.values(input.notebookLayout).some(v => !record(v) || typeof v.imageScale !== 'number' || !Number.isFinite(v.imageScale) || v.imageScale < .1 || v.imageScale > 10))) throw new Error('图片缩放须在 0.1–10 之间')
  const progress = input.notebookProgress
  if (progress !== undefined && (!record(progress) || progress.lastQuestionId !== undefined && typeof progress.lastQuestionId !== 'string' || progress.resetAt !== undefined && (!record(progress.resetAt) || Object.values(progress.resetAt).some(v => typeof v !== 'number' || !Number.isFinite(v) || v < 0)))) throw new Error('错题本进度格式无效')
}
export const notebookBodyAnchor=(id:string)=>'__question_body__:'+id
export const notebookQuestionAnchor=(anchor:string)=>anchor.startsWith('__question_body__:')?anchor.slice('__question_body__:'.length):anchor
export function favoriteNotebookTitle(title:string){const suffix='·⭐️错题精选';return title.trim().slice(0,120-suffix.length)+suffix}
export function parseQuestionRange(value: string, total: number): number[] {
  const selected = new Set<number>()
  for (const token of value.replace(/[，、；;]/g, ',').split(',').map(s => s.trim()).filter(Boolean)) {
    const match = /^(\d+)(?:\s*[-–—~至]\s*(\d+))?$/.exec(token)
    if (!match) throw new Error('题号格式示例：1–10, 15, 20–25')
    const from = Number(match[1]), to = Number(match[2] || match[1])
    if (from < 1 || to < from || to > total) throw new Error(`题号范围应在 1–${total} 之间`)
    for (let n = from; n <= to; n++) selected.add(n - 1)
  }
  if (!selected.size) throw new Error('请选择至少一道题目')
  return [...selected].sort((a, b) => a - b)
}
